import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Home, Building2, PackageCheck, Sparkles, Truck } from "lucide-react";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { StickyCTA } from "@/components/StickyCTA";
import { Seo } from "@/components/Seo";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { CtaBanner } from "@/components/CtaBanner";
import { MediaThumb } from "@/components/MediaPanel";
import { services, areas } from "@/data/seo";

const ICONS = {
  "house-removals": Home,
  "office-removals": Building2,
  "packing": PackageCheck,
  "end-of-tenancy-cleaning": Sparkles,
  "man-and-van": Truck,
};

export const ServicesIndex = () => (
  <main className="bg-white min-h-screen">
    <Seo
      title="Removals, Packing & Cleaning Services in Cardiff | BoxB Solutions"
      description="Every service you need for a stress-free move in Cardiff & South Wales — house removals, office moves, packing, end of tenancy cleaning and man and van."
      canonical="/services"
    />
    <Nav />
    <section className="pt-28 md:pt-32 pb-16">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <Breadcrumbs trail={[
          { href: "/", label: "Home" },
          { href: "/services", label: "Services" },
        ]} />
        <h1 className="mt-8 font-display font-bold text-[#0A192F] text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-3xl">
          Our services in Cardiff &amp; South Wales
        </h1>
        <p className="mt-5 text-lg text-slate-600 max-w-2xl">
          Choose a service to see local pricing, coverage areas and FAQs. Every service is fully insured and comes with a fixed-price guarantee.
        </p>

        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Object.values(services).map((s) => (
            <Link key={s.slug} to={`/services/${s.slug}`} data-testid={`services-card-${s.slug}`}
                  className="group rounded-3xl border border-slate-200 bg-white overflow-hidden hover-lift hover:border-[#0A192F] transition-colors">
              <div className="relative h-40 overflow-hidden">
                {s.image ? (
                  <img src={s.image} alt={`${s.name} by BoxB Solutions Cardiff`} loading="lazy" className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                ) : (
                  <div className="p-4"><MediaThumb variant={s.slug} icon={ICONS[s.slug]} /></div>
                )}
              </div>
              <div className="p-6">
                <span className="text-xs tracking-[0.22em] uppercase font-bold text-[#2563EB]">{s.tag}</span>
                <h2 className="mt-2 font-display font-semibold text-2xl text-[#0A192F]">{s.name}</h2>
                <p className="mt-2 text-sm text-slate-600 line-clamp-3">{s.metaDescription}</p>
                <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-[#0A192F] group-hover:text-[#2563EB]">
                  Explore {s.shortName} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
    <CtaBanner />
    <Footer />
    <StickyCTA />
  </main>
);

export const AreasIndex = () => (
  <main className="bg-white min-h-screen">
    <Seo
      title="Areas We Cover in Cardiff & South Wales | BoxB Solutions"
      description="BoxB Solutions serves Cardiff, Newport, Swansea, Barry, Penarth, Caerphilly, Pontypridd and Bridgend. Free quote in minutes on WhatsApp."
      canonical="/areas"
    />
    <Nav />
    <section className="pt-28 md:pt-32 pb-16">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <Breadcrumbs trail={[
          { href: "/", label: "Home" },
          { href: "/areas", label: "Areas" },
        ]} />
        <h1 className="mt-8 font-display font-bold text-[#0A192F] text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-3xl">
          Areas we cover across South Wales
        </h1>
        <p className="mt-5 text-lg text-slate-600 max-w-2xl">
          Choose your location for a page with local coverage, nearby neighbourhoods and pricing.
        </p>

        <div className="mt-12 grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {Object.values(areas).map((a) => (
            <Link key={a.slug} to={`/areas/${a.slug}`}
                  className="rounded-3xl border border-slate-200 bg-white p-6 hover-lift hover:border-[#0A192F] transition-colors">
              <div className="font-display font-semibold text-xl text-[#0A192F]">{a.name}</div>
              <div className="mt-1 text-xs text-slate-500">{a.landmarks.slice(0,3).join(" · ")}</div>
              <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#0A192F]">
                Local page <ArrowRight className="h-4 w-4" />
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
    <CtaBanner />
    <Footer />
    <StickyCTA />
  </main>
);
